/**
 * The acquisition schedule as the platform reads it, mirroring the backend's
 * `parseSchedule` (apps/backend/src/monitoring/schedule.ts). Four cron shapes
 * are understood — every n minutes, every n hours, every hour, once a day —
 * and the labels here
 * are word for word the ones the monitoring grid is captioned with, so the
 * schedule button and the grid never seem to disagree.
 *
 * Anything else is still a schedule the hub will happily run; it is simply one
 * we can draw no grid for, which is what `describeCron` returning null means.
 * Times are UTC throughout, like everything else on this page.
 */

export type CronShape =
    | { kind: 'minutes'; minutes: number }
    // `minute` is the offset within the hour the run happens at, which every
    // n-hour cron carries and most of ours leave at 0.
    | { kind: 'hours'; hours: number; minute: number }
    | { kind: 'daily'; hour: number; minute: number };

const EVERY_N_MINUTES_RE = /^\*\/(\d{1,2}) \* \* \* \*$/;
const EVERY_N_HOURS_RE = /^(\d{1,2}) \*\/(\d{1,2}) \* \* \*$/;
const EVERY_HOUR_RE = /^(\d{1,2}) \* \* \* \*$/;
const ONCE_A_DAY_RE = /^(\d{1,2}) (\d{1,2}) \* \* \*$/;

/**
 * One cron field: a number, a `*`, a range, any of those stepped, or a list of
 * them. Shape only — the same check the backend runs before it writes the value
 * to the hub, so what this accepts is what will save.
 */
const CRON_FIELD = String.raw`(\*|\d{1,2}(-\d{1,2})?)(\/\d{1,2})?(,(\*|\d{1,2}(-\d{1,2})?)(\/\d{1,2})?)*`;
const CRON_RE = new RegExp(`^${CRON_FIELD}( ${CRON_FIELD}){4}$`);

const pad2 = (n: number) => String(n).padStart(2, '0');

/** Single-space a typed cron, the way the backend stores it. */
export const normalizeCron = (cron: string): string =>
    cron.trim().replace(/\s+/g, ' ');

/** Whether the backend will accept this as a cron expression at all. */
export const isCronSyntax = (cron: string): boolean =>
    CRON_RE.test(normalizeCron(cron));

/** Which of the three drawable shapes this cron is, or null for none of them. */
export function parseCron(cron: string): CronShape | null {
    const everyNMinutes = cron.match(EVERY_N_MINUTES_RE);
    if (everyNMinutes) {
        const minutes = parseInt(everyNMinutes[1], 10);
        // The minute field is 0-59: a step of 60 or more fires once an hour at
        // best, and cron won't take it.
        if (minutes < 1 || minutes > 59) return null;
        return { kind: 'minutes', minutes };
    }

    const everyNHours = cron.match(EVERY_N_HOURS_RE);
    if (everyNHours) {
        const minute = parseInt(everyNHours[1], 10);
        const hours = parseInt(everyNHours[2], 10);
        if (minute > 59 || hours < 1 || hours > 24) return null;
        return { kind: 'hours', hours, minute };
    }

    // "<min> * * * *" — every hour, which is "*/1" said the other way round.
    const everyHour = cron.match(EVERY_HOUR_RE);
    if (everyHour) {
        const minute = parseInt(everyHour[1], 10);
        if (minute > 59) return null;
        return { kind: 'hours', hours: 1, minute };
    }

    const onceADay = cron.match(ONCE_A_DAY_RE);
    if (onceADay) {
        const minute = parseInt(onceADay[1], 10);
        const hour = parseInt(onceADay[2], 10);
        if (minute > 59 || hour > 23) return null;
        return { kind: 'daily', hour, minute };
    }

    return null;
}

/** The grid's own wording for a schedule, or null when it has none for it. */
export function describeCron(cron: string): string | null {
    const shape = parseCron(normalizeCron(cron));
    if (!shape) return null;
    if (shape.kind === 'minutes') {
        return shape.minutes === 1
            ? 'every minute'
            : `every ${shape.minutes}min`;
    }
    if (shape.kind === 'hours') {
        if (shape.hours === 24) return 'daily';
        return shape.hours === 1 ? 'hourly' : `every ${shape.hours}h`;
    }
    return `daily at ${pad2(shape.hour)}:${pad2(shape.minute)}`;
}

/**
 * "Every n hours", on the hour: what the project-side picker also produces.
 * Every hour is written plainly, so opening the form on a hub already on
 * `0 * * * *` and saving doesn't rewrite its config to say the same thing.
 */
export const hoursToCron = (hours: number): string =>
    hours === 1 ? '0 * * * *' : `0 */${hours} * * *`;

/** "Every day at hh:mm" (UTC). */
export const dailyToCron = (hour: number, minute: number): string =>
    `${minute} ${hour} * * *`;
